import React, { forwardRef } from "react";

import { CheckoutElementsProvider, StripeUseCheckoutElementsResult, useCheckoutElements } from "@stripe/react-stripe-js/checkout";
import { Appearance, Stripe } from "@stripe/stripe-js";

import Spinner from "js/react/components/Spinner";

import { appearanceA, appearanceB, defaultAppearance } from "./appearance";

type BaseComponent = React.ComponentType<any>;
type InjectedProps = { stripe: Stripe, checkoutElements: StripeUseCheckoutElementsResult };
type WrapperProps<T extends BaseComponent> = Omit<React.ComponentProps<T>, keyof InjectedProps> & Partial<InjectedProps>;

export interface WithCheckoutSessionOptions {
    appearance?: Appearance;
    appearanceVariant?: "A" | "B";
    adaptivePricing?: boolean;
}

export default function WithCheckoutSession<T extends BaseComponent>(Component: T, options?: WithCheckoutSessionOptions) {
    const ComponentWithCheckoutSession = forwardRef<T, WrapperProps<T>>(function ComponentWithCheckoutSession(props: React.ComponentProps<T> & { stripe: Stripe }, ref: React.Ref<T>) {
        const checkoutElements = useCheckoutElements();

        if (!checkoutElements || checkoutElements.type !== "success") {
            return <Spinner />;
        }

        // @ts-ignore
        return <Component {...props} checkoutElements={checkoutElements} ref={ref} />;
    });

    return forwardRef<T, WrapperProps<T> & { clientSecret: string }>(function Wrapper(props: WrapperProps<T> & { clientSecret: string }, ref) {
        const { stripe, clientSecret } = props;

        return (
            <CheckoutElementsProvider
                stripe={stripe}
                options={{
                    clientSecret,
                    elementsOptions: {
                        fonts: [{ cssSrc: "https://fonts.googleapis.com/css?family=Source+Sans+Pro&display=swap" }],
                        appearance: options?.appearance ?? (
                            options?.appearanceVariant === "A" ? appearanceA : options?.appearanceVariant === "B" ? appearanceB : defaultAppearance
                        )
                    },
                    adaptivePricing: { allowed: options?.adaptivePricing ?? true }
                }}
            >
                <ComponentWithCheckoutSession {...props} stripe={stripe} ref={ref} />
            </CheckoutElementsProvider>
        );
    });
}
